import { useCallback, useEffect, useMemo, useState } from 'react'
import Layout from '@/components/layout/layout'
import { Box, Flex, Grid, Heading, Separator } from '@chakra-ui/react'
import ButtonMore from '@/components/layout/action/button-more'
import StockProductCard from '@/components/cards/stock-products-card'
import ModalActionsDashboard from '@/components/modal/modal-actions-dashboard'
import JwtPayload from '@/entities/api/jwt'
import SpinnerLoading from '@/components/elements/spinner'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch, RootState } from '@/store/store'
import { Entities } from '@/entities/api/entities'
import Pagination from '@/components/elements/pagination'
import Toast from '@/components/elements/toast'
import { sessionRefrestUpdate } from '@/store/features/state'
import { setProductsFn, setSelectProductFn } from '@/store/features/products'
import ProductEntity from '@/entities/api/products/products'
import { fetchProducts } from '@/services/api/products/products'
import NotData from '@/components/elements/not-data'
import Search from '@/components/elements/search'
import Filter from '@/components/elements/filter'
import { filtersMapCategories } from '@/utils/maps/categories-maps'
import { setCategoriesFn } from '@/store/features/categories'
import { fetchCategories } from '@/services/api/categories/categories'
import { usePagination } from '@/hooks/usePagination'

const Stock = ({ data }: { data: JwtPayload }) => {
	const [modalAddOpen, setmodalAddOpen] = useState<Boolean>(false)
	const [modalEditOpen, setmodalEditOpen] = useState<Boolean>(false)
	const [isLoading, setIsloading] = useState<boolean>(false)
	const [search, setSearch] = useState<string>()
	const [productsFilter, setProductsFilter] = useState<ProductEntity[] | []>()
	const [filter, setFilter] = useState<Entities>(Entities.ALL)
	const token = useSelector((state: RootState) => state.auth.token)
	const productsRefrest = useSelector(
		(state: RootState) => state.state.productsRefrest
	)
	const categories = useSelector(
		(state: RootState) => state.categories.categories
	)
	const products = useSelector((state: RootState) => state.products.products)
	const dispatch = useDispatch<AppDispatch>()
	const [isLastPage, setIsLastPage] = useState<boolean>(false)
	const categoriesMap = useMemo(
		() =>
			categories.map((category) => ({
				name: category.name,
				label: category.name,
				value: category.name,
			})),
		[categories]
	)
	const {
		limit,
		offset,
		nextPage,
		prevPage,
		goToPage,
		setItemsPerPage,
		counterPages,
		isLoading: isPaginationLoading,
		setIsLoading: setPaginationLoading,
		currentPage,
		isFirstPage,
		handleEmptyResponse,
	} = usePagination({
		initialLimit: 10,
	})

	const fetchProductsData = useCallback(async () => {
		try {
			setIsloading(true)
			setPaginationLoading(true)

			const response = await fetchProducts({
				token: token ?? '',
				limit,
				offset,
			})

			if (response?.status === 401) {

				dispatch(sessionRefrestUpdate())
				return
			}

			if (
				!response?.data ||
				response.data.length === 0 ||
				response.data.length < limit
			) {
				setIsLastPage(true)

				if (response?.data?.length === 0 && offset > 0) {
					handleEmptyResponse()
					return
				}
			} else {
				setIsLastPage(false)
			}

			dispatch(setProductsFn(response?.data ?? []))
			setProductsFilter(response?.data ?? [])

			if (categories.length === 0) {
				const responseCategories = await fetchCategories(token ?? '')
				if (responseCategories?.status === 401) {
					
					dispatch(sessionRefrestUpdate())
					return
				}
				dispatch(setCategoriesFn(responseCategories?.data ?? []))
			}
		} catch (error) {
			Toast({ type: 'error', message: 'No se pudo obtener los productos' })
			if (offset > 0) {
				handleEmptyResponse()
			}
		} finally {
			setIsloading(false)
			setPaginationLoading(false)
		}
	}, [token, limit, offset, categories.length, setPaginationLoading])

	useEffect(() => {
		fetchProductsData()
	}, [fetchProductsData, productsRefrest])

	useEffect(() => {
		setProductsFilter(products)
	}, [products])

	useEffect(() => {
		if (search && search.trim().length > 0) {
			const term = search.toLowerCase()
			setProductsFilter(
				products?.filter(
					(pro) =>
						pro.name.toLowerCase().includes(term) ||
						pro.type.toLowerCase().includes(term) ||
						pro.category.name.toLowerCase().includes(term)
				) ?? []
			)
			return
		}

		if (filter && filter !== Entities.ALL) {
			setProductsFilter(
				products?.filter(
					(product) =>
						product.type === filter || product.category.name === filter
				) ?? []
			)
			return
		}

		setProductsFilter(products ?? [])
	}, [search, filter, products])

	const filterOpen = (value: Entities) => {
		setFilter(value)
		setSearch('')
	}

	return (
		<Layout data={data}>
			{isLoading && !isPaginationLoading ? (
				<SpinnerLoading />
			) : (
				<>
					<Flex w='full' p='4' direction='column' gap='6'>
						<Heading
							as='h2'
							color='black'
							fontSize={{ base: '2xl', sm: '3xl' }}
							textAlign={{ base: 'center', lg: 'start' }}
							fontWeight='800'
							whiteSpace='normal'
							wordBreak='break-word'
						>
							Inventario de productos
						</Heading>

						<Flex
							w='full'
							direction={{ base: 'column', lg: 'row' }}
							align='center'
							justify='space-between'
							gap='4'
							flexWrap='wrap'
						>
							<ButtonMore click={() => setmodalAddOpen(true)} />

							<Flex
								w={{ base: 'full', lg: 'auto' }}
								wrap='wrap'
								gap='4'
								justify={{ base: 'start', md: 'center' }}
							>
								<Box w={{ base: 'full', sm: 'auto' }}>
									<Search setSearch={setSearch} />
								</Box>

								<Box w={{ base: 'full', sm: 'auto' }}>
									<Filter
										label='Tipo :'
										filters={filtersMapCategories}
										setFilter={filterOpen}
									/>
								</Box>

								<Box w={{ base: 'full', sm: 'auto' }}>
									<Filter
										label='Categoria :'
										filters={categoriesMap}
										setFilter={filterOpen}
									/>
								</Box>
							</Flex>
						</Flex>
					</Flex>

					<Separator w='full' />

					<Box w='full' p='6' position='relative'>
						{isPaginationLoading && <SpinnerLoading size='lg' />}

						{!isPaginationLoading && productsFilter && productsFilter.length > 0 ? (
							<Grid
								w='full'
								templateColumns={{
									base: '1fr',
									sm: 'repeat(2, 1fr)',
									lg: 'repeat(3, 1fr)',
									xl: 'repeat(4, 1fr)',
								}}
								gap='4'
								gridAutoRows='auto'
								alignItems='start'
							>
								{productsFilter.map((product, index) => (
									<StockProductCard
										key={index}
										product={product}
										token={token ?? ''}
										click={() => {
											dispatch(setSelectProductFn(product as ProductEntity))
											setmodalEditOpen(true)
										}}
									/>
								))}
							</Grid>
						) : (
							<NotData />
						)}

						<Pagination
							totalPages={Math.ceil(products.length / limit)}
							limit={limit}
							offset={offset}
							totalItems={products.length}
							counterPages={counterPages}
							onPrevPage={prevPage}
							onNextPage={nextPage}
							onGoToPage={goToPage}
							onSetItemsPerPage={setItemsPerPage}
							isLoading={isPaginationLoading}
							currentPage={currentPage}
							isFirstPage={isFirstPage}
							isLastPage={isLastPage}
						/>
					</Box>

					{modalAddOpen && (
						<ModalActionsDashboard
							open={modalAddOpen}
							action='add-stock'
							close={() => setmodalAddOpen(false)}
						/>
					)}
					{modalEditOpen && (
						<ModalActionsDashboard
							open={modalEditOpen}
							action='edit-stock'
							close={() => setmodalEditOpen(false)}
						/>
					)}
				</>
			)}
		</Layout>
	)
}

export default Stock
